import axios, { AxiosResponse, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { 
  handleUnauthorized, 
  handleForbidden, 
  handleNotFound, 
  handleGenericError,
  showNetworkError,
  extractErrorMessage
} from './error_handler';
import { ErrorResponse, ClientData } from './axios_types';

function setupRequestInterceptor() {
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      addClientHeader(config);
      return config;
    },
    (error) => {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  );
}

function addClientHeader(config: AxiosRequestConfig) {
  try {
    const clientJson = localStorage.getItem('currentClient');
    if (clientJson) {
      const clientData: ClientData = JSON.parse(clientJson);
      if (!config.headers) {
        config.headers = {};
      }
      config.headers['X-Company-Id'] = clientData.workspace;
    }
  } catch (error) {
    console.warn('Failed to parse client data:', error);
  }
}

function setupResponseInterceptor() {
  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError<ErrorResponse>) => handleResponseError(error)
  );
}

function isApiRequest(config: any): boolean {
  const url = config?.url || '';
  
  // Проверяем базовые пути API
  const apiPaths = ['api/', '/graphql'];
  
  return apiPaths.some(path => url.includes(path)) || 
    // Проверяем если baseURL содержит API
    (config?.baseURL && apiPaths.some(path => config.baseURL.includes(path)));
}

function handleResponseError(error: AxiosError<ErrorResponse>) {
  // Обработка сетевых ошибок
  if (!error.response) {
    handleNetworkError(error);
    return Promise.reject(error);
  }

  const { status, data } = error.response;
  const shouldShowError = !data?.no_show;
  const isApi = isApiRequest(error.config);

  switch (status) {
    case 401:
      handleUnauthorized();
      return Promise.reject(error);

    case 403:
      handleForbidden();
      return Promise.reject(error);

    case 404:
      handleNotFound(isApi);
      if (isApi) {
        // Для API возвращаем ошибку чтобы компонент мог её обработать
        const notFoundMessage = extractErrorMessage(data || { message: 'Ресурс не найден' });
        const notFoundError = new Error(notFoundMessage);
        (notFoundError as any).response = error.response;
        (notFoundError as any).status = status;
        return Promise.reject(notFoundError);
      }
      break;
    default: {
      const errorMessage = handleGenericError(data || {}, shouldShowError);
      const customError = new Error(errorMessage);
      // Сохраняем оригинальную информацию об ошибке
      (customError as any).response = error.response;
      (customError as any).status = status;
      return Promise.reject(customError);
    }
  }

  return Promise.reject(error);
}

function handleNetworkError(error: AxiosError) {
  const config = error.config as any;
  const errorMessage = `${error.message} at ${config?.method?.toUpperCase()} ${config?.url}`;
  
  console.error('Network error:', errorMessage);
  showNetworkError();
}

export function setupHttpInterceptor() {
  setupRequestInterceptor();
  setupResponseInterceptor();
}